import { ClubLinkButton } from './ClubButton'
import { EmptyPlate } from './EmptyPlate'

interface BookGateProps {
  /** Whether the viewer holds a session at all (`useAuth().isAuthenticated`). */
  isAuthenticated: boolean
  /** The club's own refusal, straight off the reading hook — never an error. */
  membershipRequired: boolean
  /** A genuine fault reading the book: a message, or `null`. */
  error: string | null
  emptyHeadingLevel?: 2 | 3
  /** The book itself. Rendered only once the club has said it may be read. */
  children: React.ReactNode
}

/**
 * What stands in front of the club's book on a clubhouse page, in the three
 * cases where there is no book to show: a visitor, a standing that does not
 * open it, and a read that failed.
 *
 * The gate lives on the DATA, never on the door (Fireside::MembershipGate, and
 * ClubhouseLayout's deliberate non-gating in clubhouseGate.test.tsx). Every
 * clubhouse PAGE stays open — chrome, rooms and all — so a lapsed member can
 * always walk to the account page that fixes their dues. Only the part fetched
 * from a membership-gated endpoint is replaced, and it is replaced by an
 * invitation rather than by a redirect.
 *
 * Two refusals worth keeping straight, because they read alike and are not:
 *   * `membershipRequired` is the club ANSWERING (a 403 `membership_required`).
 *     It is not an error, it raises no `role="alert"`, and it never links to
 *     checkout — a member behind on dues already holds a standing, and selling
 *     them a second one is the wrong action in Stripe and the wrong sentence.
 *   * `error` is a fault — the network, or the club — and is the only state
 *     here that a screen reader is interrupted for.
 *
 * The copy is the club's, not the caller's, so that every surface that reads
 * the book says the same words about it. A page that needs to say something
 * different about being signed out should pass it in rather than diverge here.
 *
 * And the words promise only what the web club can keep. It used to invite a
 * visitor to "add yours to the register" — but the web club is READ-ONLY and
 * Caddie is the only writer (club-content-inventory.md §3, club-web-decisions.md),
 * so signing in adds nothing anywhere; it opens the book to read. The sentence
 * says that, in the same shape as `SignInPrompt`'s "Sign in to see …".
 */
export function BookGate({ isAuthenticated, membershipRequired, error, emptyHeadingLevel, children }: BookGateProps) {
  if (!isAuthenticated) {
    return (
      <EmptyPlate
        headingLevel={emptyHeadingLevel}
        title="The book is not yet open."
        description="Rounds appear here as members play them. Sign in to read the book."
        action={
          <ClubLinkButton to="/sign-in" variant="ghost" size="sm">
            Sign in
          </ClubLinkButton>
        }
      />
    )
  }

  if (membershipRequired) {
    return (
      <EmptyPlate
        headingLevel={emptyHeadingLevel}
        title="The book is kept for members in good standing."
        description="Your standing does not open the register just now. Your account page has the state of your dues, and the way to settle them."
        action={
          <ClubLinkButton to="/clubhouse/account" variant="ghost" size="sm">
            Your account
          </ClubLinkButton>
        }
      />
    )
  }

  if (error) {
    return (
      <p role="alert" className="text-[13.5px] text-flag">
        {error}
      </p>
    )
  }

  return <>{children}</>
}
